import { useEffect, useRef, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import {
  useCountRankHistoricalQuery,
  useGetRankHistoricalMutation
} from '@/app/KeywordTracker/api/rankHistoricalApi';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import { useExportTableMutation } from '@/app/KeywordTracker/api/exportApi';
import { useGetTariffInfoQuery } from '@/app/KeywordTracker/api/keywordsApi';
import { competitorsColumnForFilterConfig } from '@/app/KeywordTracker/config/ranksHistoricalColumnConfig';
import { useGetLocationsForSelectQuery } from '@/app/KeywordTracker/api/locationsApi';
import AgGridSkeleton from '@/app/common/components/skeleton/AgGridSkeleton';
import TextLoading from '@/app/common/components/skeleton/TextLoading';
import TimePickerSpecific from '@/app/KeywordTracker/components/common/TimePickerSpecific';
import AgGridWrapper from '@/app/common/components/ag-grid/AgGridWrapper';
import { ranksHistoricalColumnConfig } from '@/app/KeywordTracker/config/ranksHistoricalColumnConfig';
import FiltersComponent from '@/app/common/components/filters/FiltersComponent';
import FiltersComponentSkeleton from '../../common/components/filters/FiltersComponentSkeleton';
import useDomain from '@/hooks/useDomain';
import { breakpointsMediaQuery, useMediaQuery } from '@/helpers/utils';
import TrackerKeywordsInactiveAlert from '@/app/KeywordTracker/components/TrackerKeywordsInactiveAlert';
import TrackerVideoPreload from '@/app/KeywordTracker/components/common/TrackerVideoPreload';
import { isTrackerKeywordsTariffZero } from '@/app/KeywordTracker/utils/trackerTariff';
import type { OutletContext } from './pageTypes';
import DataLoadError from '@/components/errors/DataLoadError';
import {
  shouldShowDataLoadError,
  isNetworkApiError
} from '@/app/common/utils/apiErrorUtils';

const RankHistorical = () => {
  const isMdUp = useMediaQuery(breakpointsMediaQuery.md);
  const gridRef = useRef<HTMLDivElement>(null);
  const [getRankHistorical] = useGetRankHistoricalMutation();
  const [searchParams] = useSearchParams();
  const { domain_slug } = useDomain();
  const { data: trackerInfo, isFetching: isFetchingTrackerInfo } =
    useGetTariffInfoQuery(domain_slug);
  const [, setBreadcrumbData] = useOutletContext<OutletContext>();
  const [updateTime, setUpdateTime] = useState<number | null>(null);
  const [exportTable] = useExportTableMutation();

  const { isLoading: isLoadingLocations } = useGetLocationsForSelectQuery();

  useEffect(() => {
    setBreadcrumbData([{ text: 'آرایش رتبه', href: '#' }]);
  }, [setBreadcrumbData]);

  const { data, isLoading, isFetching, isError, error, refetch } =
    useCountRankHistoricalQuery({
      page: 1,
      to_date: Number(updateTime ? updateTime : 0),
      perPage: 50,
      domain_slug
    });

  const { data: locations } = useGetLocationsForSelectQuery();

  useEffect(() => {
    if (!isLoading && data) {
      setUpdateTime(data?.data?.to_date ? data?.data?.to_date : 0);
    }
  }, [data, isLoading]);

  const isTrackerInactive =
    !isFetchingTrackerInfo && isTrackerKeywordsTariffZero(trackerInfo);

  return (
    <Row>
      {!isTrackerInactive && (
        <Row className="g-0 my-3 p-0 px-3 justify-content-between">
          <Col sm="auto"></Col>
          <Col md="auto" className="mb-3 mb-md-0">
            <Row className="g-2 gy-md-0 h-100 align-items-center">
              <Col xs="auto">
                <h6 className="text-700 mb-0 text-nowrap">بازه زمانی</h6>
              </Col>
              <Col xs={12} md="auto">
                {data && !isFetching ? (
                  <TimePickerSpecific
                    time={updateTime}
                    setTime={setUpdateTime}
                    availableDays={[]}
                  />
                ) : (
                  <TextLoading
                    width={isMdUp ? 205 : 100}
                    height={24}
                    isWidthPx={isMdUp}
                  />
                )}
              </Col>
            </Row>
          </Col>
        </Row>
      )}
      {isTrackerInactive ? (
        <Col xxl={12}>
          <TrackerKeywordsInactiveAlert />
          <TrackerVideoPreload
            src="https://help.mizfa.tools/wp-content/uploads/2025/01/arayeshrotbeh.mp4"
            autoPlay
            loop
            muted
          />
        </Col>
      ) : shouldShowDataLoadError(isError, error) ? (
        <Col xxl={12}>
          <DataLoadError
            error={error}
            onRetry={() => refetch()}
            isRetrying={isFetching}
            isNetworkError={isNetworkApiError(error)}
          />
        </Col>
      ) : (
        <>
          <div className="mb-3">
            {!isFetching && !isLoadingLocations ? (
              <FiltersComponent
                preQueries={
                  searchParams.get('to_date')
                    ? `to_date=${searchParams.get('from_date')}`
                    : ''
                }
                columns={competitorsColumnForFilterConfig()}
                selectItems={{
                  device_type: [
                    { value: 1, label: 'رایانه' },
                    { value: 2, label: 'گوشی' }
                  ],
                  location: locations?.data
                }}
                total={data?.data?.total}
              />
            ) : (
              <FiltersComponentSkeleton />
            )}
          </div>
          {!isFetching ? (
            <>
              <Col>
                <div ref={gridRef}>
                  <AgGridWrapper
                    settings={{
                      pagination: true,
                      fullscreen: false,
                      changeHeight: false,
                      resetGrid: false,
                      saveGrid: false,
                      additionalData: { domain_slug },
                      export: {
                        enabled: true,
                        fetchFunction: exportTable
                      }
                    }}
                    table_name={location.pathname
                      .split('/')
                      .slice(1, 3)
                      .join('/')}
                    columns={ranksHistoricalColumnConfig(
                      data?.data?.columns,
                      isMdUp
                    )}
                    total={data?.data?.total}
                    additionalData={{
                      domain_slug,
                      to_date: Number(updateTime ? updateTime : 0)
                    }}
                    data={data?.data?.rows}
                    height="700px"
                    fetchFunction={getRankHistorical}
                  />
                </div>
              </Col>
            </>
          ) : (
            <>
              <Col xxl={12}>
                <AgGridSkeleton
                  height="650px"
                  settings={{
                    pagination: false,
                    fullscreen: false,
                    changeHeight: false,
                    resetGrid: false,
                    saveGrid: false
                  }}
                />
              </Col>
            </>
          )}
        </>
      )}
    </Row>
  );
};

export default RankHistorical;
